<template>
    <!-- watermark -->
    <div v-if="props.wathermark"
        class="wathermark"
        :style="{ 'aspect-ratio': props.aspectRatio }"
        :class="props.wathermark"
    >
        <img
            v-if="props.preview"
            :src="src"
            :alt="props.alt"
            crossorigin="anonymous"
            @load="handleLoad"
            @error="handleError"
        />
        <span></span>
        <label>
            <slot>{{ props.alt }}</slot>
        </label>
    </div>

    <!-- skeleton -->
    <div v-else-if="props.skeleton"
        class="skeleton-wrapper"
        :style="{ 'aspect-ratio': props.aspectRatio }"
        :class="{ loaded: isLoaded }"
    >
        <img
            v-if="props.preview"
            :src="src"
            :alt="props.alt"
            crossorigin="anonymous"
            @load="handleLoad"
            @error="handleError"
        />
    </div>

    <!-- обычное изображение -->
    <img
        v-else
        :src="src"
        :alt="props.alt"
        :style="{ 'aspect-ratio': props.aspectRatio }"
        crossorigin="anonymous"
        @load="handleLoad"
        @error="handleError"
    />
</template>

<script setup>
import { ref, computed } from 'vue';

const props = defineProps({
    preview: { type: String, default: '' },
    original: { type: String, default: '' },
    alt: { type: String, default: '' },
    wathermark: { type: [String, null], default: null },
    aspectRatio: { type: [String, null], default: null },
    skeleton: { type: Boolean, default: false },
    checktone: { type: Boolean, default: false }
});

const emit = defineEmits(['load', 'error', 'tone', 'color']);

const isLoaded = ref(false);

const checkTone = (e) => {
    const image = e.target;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const sampleWidth = 100;
    const aspectRatio = image.naturalHeight / image.naturalWidth;
    canvas.width = sampleWidth;
    canvas.height = sampleWidth * aspectRatio;

    ctx?.drawImage(image, 0, 0, canvas.width, canvas.height);

    try {
        const imageData = ctx?.getImageData(0, canvas.height * 0.66, canvas.width, canvas.height * 0.33);
        if (!imageData) return;

        const data = imageData.data;
        const pixelCount = data.length / 4;

        let totalLuminance = 0;
        let totalR = 0;
        let totalG = 0;
        let totalB = 0;

        for (let i = 0; i < data.length; i += 4) {
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];

            totalR += r;
            totalG += g;
            totalB += b;

            // яркость W3C
            totalLuminance += 0.2126 * r + 0.7152 * g + 0.0722 * b;
        }

        // средний цвет
        const avgR = Math.round(totalR / pixelCount);
        const avgG = Math.round(totalG / pixelCount);
        const avgB = Math.round(totalB / pixelCount);

        emit('color', {r: avgR, g: avgG, b: avgB});

        // определяем тон
        const avgLuminance = totalLuminance / pixelCount;
        const tone = avgLuminance < 128 ? 'dark' : 'light';

        emit('tone', tone);

    } catch (err) {}
}

function handleLoad(e) {
    isLoaded.value = true;

    if (props.checktone) {
        checkTone(e);   
    }

    emit('load', e);
}

function handleError(e) {
    // isLoaded.value = true;
    emit('error', e);
}

const src = computed(() => {
    return `${props.preview?.indexOf('http') === -1 ? import.meta.env.VITE_APP_FILES_URL : ''}${props.preview}`;
});
</script>

<style lang="scss" scoped>
img {
}

.skeleton-wrapper {
    position: relative;
    width: 100%;
    height: 100%;

    img {
        position: relative;
        width: 100%;
        height: 100%;
        opacity: 0;
        transition: opacity .2s;
        object-fit: cover;
        overflow: hidden;
    }

    &::after {
        content: '';
        @include skeleton;
        display: block;
        z-index: 1;
    }

    &.loaded {
        img {
            opacity: 1;
        }
    }

    &.loaded::after {
        display: none;
    }
}

.wathermark {
    position: relative;
    overflow: hidden;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    span {
        position: absolute;
        width: 100%;
        height: 100%;
        right: 0;
        bottom: 0;
        mask-repeat: no-repeat;
        mask-size: cover;
        mask-position: 0px 30px;
        -webkit-mask-repeat: no-repeat;
        -webkit-mask-size: cover;
        -webkit-mask-position: 0px 30px;
        backdrop-filter: blur(4px);
        -webkit-backdrop-filter: blur(4px);
        background: var(--dark-20);
        transition: all 0.4s;
        pointer-events: none;
    }

    &.genres span {
        mask-image: url('data:image/svg+xml;utf8,<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"><path d=\'M0,100 L0,60 C30,70 60,20 100,10 L100,100 Z\' fill="white"/></svg>');

        -webkit-mask-image: url('data:image/svg+xml;utf8,<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"><path d=\'M0,100 L0,60 C30,70 60,20 100,10 L100,100 Z\' fill="white"/></svg>');
    }

    &.categories span {
        mask-image: url('data:image/svg+xml;utf8,<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"><path d="M0,100 L0,55 C25,65 55,25 100,15 L100,100 Z" fill="white"/></svg>');

        -webkit-mask-image: url('data:image/svg+xml;utf8,<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"><path d="M0,100 L0,55 C25,65 55,25 100,15 L100,100 Z" fill="white"/></svg>');
    }

    &.moods span {
        mask-image: url('data:image/svg+xml;utf8,<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"><path d="M0,100 L0,50 C20,80 80,20 100,40 L100,100 Z" fill="white"/></svg>');
        -webkit-mask-image: url('data:image/svg+xml;utf8,<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"><path d="M0,100 L0,50 C20,80 80,20 100,40 L100,100 Z" fill="white"/></svg>');
    }

    &.playlists span {
        mask-image: url('data:image/svg+xml;utf8,<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"><path d="M100,100 L100,55 C75,65 45,25 0,15 L0,100 Z" fill="white"/></svg>');

        -webkit-mask-image: url('data:image/svg+xml;utf8,<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"><path d="M100,100 L100,55 C75,65 45,25 0,15 L0,100 Z" fill="white"/></svg>');
    }

    &.playlists-top span {
        -webkit-mask-position: 0px 0px;
        mask-image: url('data:image/svg+xml;utf8,<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"><path d="M0,0 L55,0 C65,2 65,18 55,25 C40,35 25,35 0,40 Z" fill="white"/></svg>');

        -webkit-mask-image: url('data:image/svg+xml;utf8,<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"><path d="M0,0 L55,0 C65,2 65,18 55,25 C40,35 25,35 0,40 Z" fill="white"/></svg>');
    }

    label {
        position: absolute;
        right: 8px;
        bottom: 8px;
        font: var(--font-body-1);
        color: var(--white-500);
        text-align: right;
        pointer-events: none;
        text-shadow: 0px 0px 3px var(--dark-500);
        display: flex;
        flex-direction: column;
        gap: 8px;
        justify-content: end;
        align-items: end;
    }

    &:hover {
        span {
            backdrop-filter: blur(8px);
            -webkit-backdrop-filter: blur(8px);
        }
    }
}
</style>
